import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  OnDestroy,
  output,
  signal,
} from "@angular/core";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { WidgetHelperService } from "../../../features/dashboard/services/widget-helper.service";
import { IconService } from "../../services/icon.service";
import { ChipState } from "../../enums/chip-state.enum";
import { NgTemplateOutlet } from "@angular/common";

@Component({
  selector: "vl-chips",
  standalone: true,
  imports: [MatIcon, MatProgressSpinner, NgTemplateOutlet],
  templateUrl: "./chips.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: "./chips.component.scss",
})
export class ChipsComponent implements OnDestroy {
  iconRight = input<string>();
  iconLeft = input<string>();
  iconColor = input<string>("inherit");
  roundedValue = input<string | number>();
  disabled = input(false);
  contentHeight = input(true);
  loading = input(false);
  state = input<ChipState>("enabled");
  size = input<"big" | "small" | "tiny">("big");
  bubble = input<number | string>();
  bubbleBgClass = input("bg-secondary");
  bubbleColorClass = input("on-secondary");
  showBubbleZero = input<boolean>(false);
  stopPropagation = input<boolean>(true);
  contentOnHover = input<boolean>(false);
  isOpen = signal(false);
  showText = signal(false);
  /**
   * Faux tant que le PREMIER rendu n'est pas peint : la classe
   * `.no-transition` neutralise alors toutes les transitions CSS. Un élément
   * qui APPARAÎT ne doit jamais « pousser » depuis un état fantôme (retour UX
   * 09/07 : chips remontées par un @switch qui semblaient grandir) — les
   * transitions (hover, contentOnHover, géométrie) restent intactes ensuite.
   * Double rAF : garantit qu'un paint a eu lieu avec les styles initiaux.
   */
  transitionsReady = signal(false);

  isAnimating = signal(false);

  hovering = signal(false);
  private hoverLeaveTimer: ReturnType<typeof setTimeout> | null = null;
  private opacityTimer: ReturnType<typeof setTimeout> | null = null;
  closeGrace = signal(false);

  unclampText = signal(false);

  _stopPropagation = computed(() => {
    if (this.cursor() === "default") {
      return false;
    }
    return this.stopPropagation();
  });
  action = output();
  iconRightAction = output();
  left = computed(() => {
    const length = this.bubbleFormatted().length;
    if (length < 4) {
      return "calc(100% - 9px)";
    }
    return "calc( 100% - 14px)";
  });
  cursor = input<"pointer" | "grab" | "default">("pointer");
  /** `stacked` (2026-08-06) : texte SOUS l'icône, compact — pour les
   *  cellules étroites (ex. tableaux condensés). Miroir du type stacked
   *  de vl-big-button. */
  layout = input<"row" | "stacked">("row");

  bubbleFormatted = computed(() => {
    const value = this.bubble();
    return typeof value === "string"
      ? value
      : WidgetHelperService.formatNumeric(value, 0);
  });
  _bubbleClasses = computed(() => {
    return `${this.bubbleBgClass()} ${this.bubbleColorClass()}`;
  });
  colorClass = computed(() => {
    return `${this.disabled() ? "chips-disabled cursor-not-allowed" : "cursor-" + this.cursor()} chips-${this.state()} chips-${this.size()}${this.layout() === "stacked" ? " chips-stacked" : ""}`;
  });

  constructor() {
    // Transitions neutralisées jusqu'au premier paint (cf. transitionsReady).
    afterNextRender(() => {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => this.transitionsReady.set(true)),
      );
    });
    effect(
      () => {
        const h = this.hovering();

        this.isAnimating.set(true);

        if (h) {
          this.isOpen.set(true);
          this.showText.set(false);
          this.unclampText.set(false);
        } else {
          this.unclampText.set(false);
          this.isOpen.set(false);
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnDestroy() {
    if (this.hoverLeaveTimer) {
      clearTimeout(this.hoverLeaveTimer);
      this.hoverLeaveTimer = null;
    }
    if (this.opacityTimer) {
      clearTimeout(this.opacityTimer);
      this.opacityTimer = null;
    }
  }

  onAction($event: MouseEvent) {
    if (this._stopPropagation()) {
      $event.stopPropagation();
    }
    if (!this.disabled() && !this.loading()) {
      this.action.emit();
    }
  }

  isSvgIcon(s: string) {
    return IconService.isSvgIcon(s);
  }

  onHoverEnter() {
    if (this.hoverLeaveTimer) {
      clearTimeout(this.hoverLeaveTimer);
      this.hoverLeaveTimer = null;
    }
    if (this.opacityTimer) {
      clearTimeout(this.opacityTimer);
      this.opacityTimer = null;
    }
    this.closeGrace.set(false);
    this.hovering.set(true);
    this.isAnimating.set(true);
    this.unclampText.set(false);

    this.opacityTimer = setTimeout(() => {
      if (this.hovering() || this.closeGrace()) {
        this.showText.set(true);
      }
      this.opacityTimer = null;
    }, 100);
  }

  onHoverLeave() {
    if (this.hoverLeaveTimer) {
      clearTimeout(this.hoverLeaveTimer);
    }
    this.unclampText.set(false);

    this.closeGrace.set(true);

    this.hoverLeaveTimer = setTimeout(() => {
      this.hovering.set(false);
      this.isAnimating.set(false);
      this.hoverLeaveTimer = null;
    }, 500);
  }

  onWidthTransitionEnd(event: TransitionEvent) {
    if (event.propertyName !== "grid-template-columns") return;

    if (this.hovering()) {
      this.unclampText.set(true);
    } else {
      this.showText.set(false);
      this.closeGrace.set(false);
    }
  }

  onOpacityTransitionEnd(event: TransitionEvent) {
    if (event.propertyName !== "opacity") return;

    if (!this.hovering()) {
      this.isOpen.set(false);
    }

    this.isAnimating.set(false);
  }
}
